import React, { Component, ErrorInfo, ReactNode } from 'react';
import { debug } from '@/utils/debug';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent, CardDescription, CardFooter } from '@/components/ui/card';
import { AlertTriangle, RefreshCw, LogOut } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * AuthErrorBoundary - Specialized error boundary for authentication-related failures
 *
 * Catches errors during:
 * - Profile fetching
 * - Token refresh
 * - Session management
 *
 * Provides user-friendly recovery options for auth failures.
 */
export class AuthErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    debug.captureError(error, 'AuthErrorBoundary');
    this.setState({ errorInfo });
  }

  handleSignInAgain = (): void => {
    // Force reload to clear state and redirect to auth
    localStorage.clear();
    window.location.href = '/auth';
  };

  handleRetry = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex items-center justify-center min-h-screen w-full p-4 bg-gray-50">
          <Card className="w-full max-w-md shadow-lg border-orange-100">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <div className="p-3 bg-orange-100 rounded-full">
                  <AlertTriangle className="w-8 h-8 text-orange-600" />
                </div>
              </div>
              <CardTitle className="text-xl text-orange-700">Authentication Issue</CardTitle>
              <CardDescription>
                We encountered an issue with your session. This usually happens when your session has expired.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-blue-50 rounded-md">
                <p className="text-sm text-blue-800">
                  <strong>What happened:</strong> Your authentication session may have expired or there was a temporary connection issue.
                </p>
              </div>
            </CardContent>
            <CardFooter className="flex flex-col gap-3">
              <Button
                onClick={this.handleSignInAgain}
                variant="default"
                className="w-full gap-2"
              >
                <LogOut className="w-4 h-4" />
                Sign In Again
              </Button>
              <Button
                onClick={this.handleRetry}
                variant="outline"
                className="w-full gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Try Again
              </Button>
            </CardFooter>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}
